import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  RefreshCw,
  ArrowRight,
  Check,
  X,
  Send,
  Activity,
  Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLIENT_LOGOS = [
  { src: "https://customer-assets.emergentagent.com/job_a79e5925-a1a3-4820-87b8-9e633a28653e/artifacts/u4kowads_Raise-a-lotto-462x90.png", alt: "Raise a Lotto" },
  { src: "https://customer-assets.emergentagent.com/job_a79e5925-a1a3-4820-87b8-9e633a28653e/artifacts/vsozmu5g_SSCIC%20Logo%20Colour%20%28Large%29.png", alt: "Support Sport CIC" },
  { src: "https://customer-assets.emergentagent.com/job_a79e5925-a1a3-4820-87b8-9e633a28653e/artifacts/zrzroq42_AWL_Logo_colour.png", alt: "All Weathers Lottery" },
];

function FadeIn({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      if (response.ok && data.success) {
        toast.success(data.message || "Message sent!");
        setFormData({ name: "", email: "", company: "", message: "" });
      } else {
        toast.error(data.detail || "Failed to send. Try again.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <style>{`
        @import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap');
        .font-clash { font-family: 'Clash Display', sans-serif; }
        .font-mono-accent { font-family: 'Space Mono', monospace; }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#10B981] flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <span className="font-clash font-bold text-xl tracking-normal text-gray-900">BannerSync</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="text-gray-600 hover:text-gray-900"
              onClick={() => navigate("/login")}
              data-testid="nav-signin-btn"
            >
              Sign In
            </Button>
            <Button
              className="bg-[#10B981] text-white hover:bg-[#059669] font-semibold px-6"
              onClick={scrollToContact}
              data-testid="nav-cta-btn"
            >
              Schedule a Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════
          1. HERO — The Character's Desire
          StoryBrand: Open with what THEY want
      ═══════════════════════════════════════════ */}
      <section className="relative pt-32 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)",
          backgroundSize: "40px 40px",
        }} />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#10B981]/[0.04] rounded-full blur-[150px]" />

        <div className="relative max-w-6xl mx-auto text-center">
          <FadeIn delay={0.1}>
            <h1 className="font-clash font-bold text-4xl sm:text-5xl lg:text-6xl tracking-normal leading-[1.05] mb-6 text-gray-900">
              Create Banners Once.<br />
              Distribute And Embed.<br />
              Track Everything.<br />
              <span className="text-[#10B981]">Update Automatically.</span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
              Stop wasting hours updating banners across dozens of websites and sending emails one by one. One update in BannerSync reaches every site and every inbox instantly.
            </p>
          </FadeIn>

          <FadeIn delay={0.3}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                className="bg-[#10B981] text-white hover:bg-[#059669] font-semibold px-8 py-6 text-base shadow-lg shadow-[#10B981]/20"
                onClick={scrollToContact}
                data-testid="hero-cta-button"
              >
                Schedule a Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </FadeIn>

          <FadeIn delay={0.4}>
            <div className="mt-16 rounded-2xl overflow-hidden shadow-2xl shadow-gray-200/60 border border-gray-100 max-w-4xl mx-auto">
              <img
                src="https://customer-assets.emergentagent.com/job_a79e5925-a1a3-4820-87b8-9e633a28653e/artifacts/cuw9qo05_Firefly_-A%20diverse%20group%20of%20happy%20business%20professionals%20celebrating%20while%20looking%20at%20a%20large%20821256.png"
                alt="Team celebrating banner performance results"
                className="w-full h-auto"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          2. THE PROBLEM — The Villain
          StoryBrand: External, Internal, Philosophical
      ═══════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#10B981] mb-4 text-center">The problem</p>
            <h2 className="font-clash font-bold text-3xl sm:text-4xl tracking-normal mb-4 text-gray-900 text-center">
              Manual banner management is costing you
            </h2>
            <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16">
              If you're managing banners across multiple websites or sending email updates by hand, you already know how draining it is.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                type: "External",
                title: "Hours lost every week",
                desc: "Every content change means logging into multiple sites, downloading files, uploading new ones, updating links. Repeat for every single banner.",
              },
              {
                type: "Internal",
                title: "The frustration of missed updates",
                desc: "You know there's outdated content live right now on a partner site — but you don't have time to fix it. It gnaws at you.",
              },
              {
                type: "Philosophical",
                title: "It shouldn't be this hard",
                desc: "In a world where everything auto-syncs, why are you still copy-pasting images and sending emails manually? There has to be a better way.",
              },
            ].map((item) => (
              <FadeIn key={item.type} delay={0.1}>
                <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-md transition-all h-full">
                  <span className="font-mono-accent text-[10px] uppercase tracking-wider text-red-400 mb-3 block">{item.type} problem</span>
                  <h3 className="font-clash font-semibold text-lg text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          3. THE GUIDE — Empathy + Authority
          StoryBrand: Show you understand, then prove competence
      ═══════════════════════════════════════════ */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div>
                <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#10B981] mb-4">We get it</p>
                <h2 className="font-clash font-bold text-3xl tracking-normal mb-6 text-gray-900">
                  We built BannerSync because we had the exact same problem
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  We were managing banners across multiple partner websites, sending update emails by hand, and had zero visibility on whether anyone was actually seeing them. It was eating up hours every week.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  So we built the tool we wished existed — and now organisations just like yours use it to save time, stay current, and finally know what's working.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_a79e5925-a1a3-4820-87b8-9e633a28653e/artifacts/p2bfpewy_Firefly_Modern%20flat%20illustration%20of%20web%20add%20banners%20flowing%20from%20a%20central%20hub%20to%20multiple%20co%20821256.png"
                  alt="BannerSync - Create, distribute, and track banners"
                  className="w-full h-auto"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Authority — Trusted By */}
      <section className="py-12 border-y border-gray-100 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-gray-400 text-center mb-8">
            Trusted by
          </p>
          <Marquee gradient={false} speed={40} pauseOnHover>
            {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((logo, i) => (
              <div key={`${logo.alt}-${i}`} className="mx-12 flex items-center justify-center h-16">
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="h-12 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
                />
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          4. THE PLAN — 3 Simple Steps
          StoryBrand: Give them a plan to follow
      ═══════════════════════════════════════════ */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#10B981] mb-4 text-center">The plan</p>
            <h2 className="font-clash font-bold text-3xl sm:text-4xl tracking-normal text-center mb-4 text-gray-900">
              Getting started is simple
            </h2>
            <p className="text-gray-600 text-center max-w-xl mx-auto mb-16">
              You don't need to be technical. Here's how it works:
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Create your banner", desc: "Use our visual editor to design your banner. Upload a background, add dynamic content, customise the look." },
              { step: "2", title: "Distribute it", desc: "Embed on websites with a simple code snippet. Add email recipients to automatically receive updates." },
              { step: "3", title: "Watch it work", desc: "Your banner stays current everywhere. Track impressions, clicks, and email opens from your dashboard." },
            ].map((item) => (
              <FadeIn key={item.step} delay={0.1}>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-[#10B981] text-white font-clash font-bold text-xl flex items-center justify-center mx-auto mb-5">
                    {item.step}
                  </div>
                  <h3 className="font-clash font-semibold text-lg text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.3}>
            <div className="text-center mt-14">
              <Button
                size="lg"
                className="bg-[#10B981] text-white hover:bg-[#059669] font-semibold px-8 py-6 text-base shadow-lg shadow-[#10B981]/20"
                onClick={scrollToContact}
                data-testid="plan-cta-button"
              >
                Schedule a Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          5. FAILURE — What's at stake
          StoryBrand: Show what happens if they DON'T act
      ═══════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-red-400 mb-4">What's at stake</p>
            <h2 className="font-clash font-bold text-3xl sm:text-4xl tracking-normal mb-6 text-gray-900">
              Every day you wait, you're losing time and visibility
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-12 leading-relaxed">
              Without a system, these problems compound. Banners stay outdated. Emails don't get sent. You have no idea what's working. Your content sits stale while competitors stay fresh.
            </p>
          </FadeIn>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "Outdated banners live on partner sites",
              "Hours wasted on repetitive manual work",
              "Emails forgotten or sent inconsistently",
              "Zero data on what's performing",
            ].map((item) => (
              <FadeIn key={item} delay={0.05}>
                <div className="bg-white border border-red-100 rounded-lg p-5">
                  <X className="w-5 h-5 text-red-400 mx-auto mb-3" />
                  <p className="text-gray-700 text-sm">{item}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          6. SUCCESS — The transformation
          StoryBrand: Paint the picture of life after
      ═══════════════════════════════════════════ */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#10B981] mb-4">The result</p>
            <h2 className="font-clash font-bold text-3xl sm:text-4xl tracking-normal mb-6 text-gray-900">
              Imagine banners that just... work
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-12 leading-relaxed">
              You update once. Every website, every email, every recipient gets the latest content — automatically. You open your dashboard and see exactly who's viewing, clicking, and engaging. No more guesswork. No more wasted time.
            </p>
          </FadeIn>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              "Banners always current across every site",
              "Emails sent automatically when you update",
              "Full visibility on opens, clicks, and impressions",
              "Hours freed up every single week",
              "Your brand always looks professional",
              "GDPR compliant with one-click unsubscribe",
            ].map((item) => (
              <FadeIn key={item} delay={0.05}>
                <div className="bg-[#10B981]/5 border border-[#10B981]/15 rounded-lg p-5">
                  <Check className="w-5 h-5 text-[#10B981] mx-auto mb-3" />
                  <p className="text-gray-800 text-sm font-medium">{item}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.3}>
            <div className="mt-14">
              <Button
                size="lg"
                className="bg-[#10B981] text-white hover:bg-[#059669] font-semibold px-8 py-6 text-base shadow-lg shadow-[#10B981]/20"
                onClick={scrollToContact}
                data-testid="success-cta-button"
              >
                Schedule a Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          7. CALL TO ACTION — Direct + Transitional
          StoryBrand: Make the ask clear and easy
      ═══════════════════════════════════════════ */}
      <section id="contact" className="py-24 px-6 bg-gray-50 border-t border-gray-100">
        <div className="max-w-2xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#10B981] mb-4">Take the next step</p>
              <h2 className="font-clash font-bold text-3xl sm:text-4xl tracking-normal mb-4 text-gray-900">
                Get your time back
              </h2>
              <p className="text-gray-600">
                Tell us about your banner setup. We'll show you exactly how BannerSync can save you hours every week.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-mono-accent uppercase tracking-wider text-gray-500 mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    data-testid="contact-name-input"
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 outline-none transition-all disabled:opacity-50"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono-accent uppercase tracking-wider text-gray-500 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    data-testid="contact-email-input"
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 outline-none transition-all disabled:opacity-50"
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono-accent uppercase tracking-wider text-gray-500 mb-2">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  data-testid="contact-company-input"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 outline-none transition-all disabled:opacity-50"
                  placeholder="Your company"
                />
              </div>
              <div>
                <label className="block text-xs font-mono-accent uppercase tracking-wider text-gray-500 mb-2">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  required
                  disabled={isSubmitting}
                  data-testid="contact-message-input"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 outline-none transition-all resize-none disabled:opacity-50"
                  placeholder="Tell us about your current banner setup..."
                />
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="w-full bg-[#10B981] text-white hover:bg-[#059669] font-semibold py-6 text-base shadow-lg shadow-[#10B981]/20 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-5 h-5 mr-2" />Schedule a Demo</>
                )}
              </Button>
            </form>

            <p className="mt-8 text-center text-gray-500 text-sm">
              Or email us directly at{" "}
              <a href="mailto:hello@bannersync.co.uk" className="text-[#10B981] hover:underline font-medium">
                hello@bannersync.co.uk
              </a>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#10B981] flex items-center justify-center">
              <RefreshCw className="w-4 h-4 text-white" />
            </div>
            <span className="font-clash font-bold text-lg tracking-normal">BannerSync</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
          </div>
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} BannerSync. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
